import i18n from 'i18next'
import { observable, makeObservable, action } from 'mobx'
import * as R from 'ramda'
import { exploriTemplateAccess } from '../SurveyWizardNew/constants'

class TemplateAccessStore {
    divisionOptions = []

    portfolioOptions = []

    client = {}

    @observable checkboxOptions = {}

    @observable label = ''

    @observable updating = true

    useLocalSave = false

    initBody = undefined

    @observable hasRequiredError = false

    constructor(store, exposeUpdateBody, initBody) {
        makeObservable(this)
        this.store = store
        this.initBody = initBody
        if (exposeUpdateBody) {
            this.useLocalSave = true
            exposeUpdateBody(this.updateTemplateAccess)
        }
    }

    init = async () => {
        await this.getDivisions()
        await this.getPortfolios()
        this.defineCheckboxFields()
        this.client = {
            id: R.path(['userData', 'clientId'], this.store),
            name: R.pathOr('', ['userData', 'clientName'], this.store),
            isMultiDivision: R.pathOr(true, ['userData', 'flags', 'isMultiDivision'], this.store),
        }

        await this.populateCheckboxFields(this.initBody)
        this.label = this.client.isMultiDivision
            ? i18n.t('settings_division_access')
            : i18n.t('settings_portfolio_access')
        this.updating = false
    }

    getDivisions = async () => {
        try {
            const res = await this.store.http.get('/division')
            this.divisionOptions = res.map((d) => ({
                label: d.name,
                id: d.divisionId,
                checked: false,
            }))
        } catch (error) {
            console.error('Error fetching divisions: ', error)
        }
    }

    getPortfolios = async () => {
        try {
            const res = await this.store.http.get('/portfolio')
            this.portfolioOptions = res.map((p) => ({
                label: p.name,
                id: p.portfolioId,
                divisionId: p.divisionId,
            }))
        } catch (error) {
            console.error('Error fetching portfolios: ', error)
        }
    }

    defineCheckboxFields = () => {
        const { divisionOptions, portfolioOptions } = this
        const result = {}

        const portfoliosByDivision = new Map()

        portfolioOptions.forEach((p, _i) => {
            const { divisionId } = p
            if (divisionId) {
                if (!portfoliosByDivision.has(divisionId)) {
                    portfoliosByDivision.set(divisionId, [])
                }
                const portfolios = portfoliosByDivision.get(divisionId)
                portfolios.push({
                    name: p.label,
                    id: p.id,
                    checked: false,
                    divisionId: p.divisionId,
                })
                portfoliosByDivision.set(divisionId, portfolios)
            }
        })

        divisionOptions.forEach((d) => {
            const divisionPortfolios = portfoliosByDivision.get(d.id) || []
            result[d.id] = {
                name: d.label,
                id: d.id,
                checked: false,
                portfolios: divisionPortfolios,
            }
        })
        this.checkboxOptions = result
    }

    populateCheckboxFields = async (initBody) => {
        try {
            const accessData = initBody || await this.store.http.get(`/template/${this.store.survey.id}/access`)
            accessData.map((ad) => {
                if (ad.oid === 3) { // client oid
                    this.toggleAll(ad.rid, true)
                } else if (ad.oid === 4) { // division oid
                    this.toggleDivision(ad.rid, true)
                } else if (ad.oid === 5) { // portfolio oid
                    this.togglePortfolio(ad.rid, true)
                }
            })
        } catch (error) {
            console.error(error)
        }
    }

    @action
    toggleDivision = (rid, val) => {
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            if (divisionData.id === rid) {
                divisionData.checked = val
                divisionData.portfolios.forEach((p) => { p.checked = val })
            }
        })
    }

    @action
    togglePortfolio = (rid, val) => {
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            const portfolio = divisionData.portfolios.find((p) => p.id === rid)
            if (portfolio) portfolio.checked = val
            const areAllSelected = divisionData.portfolios.length > 0 && divisionData.portfolios.every((p) => p.checked)
            this.checkboxOptions[divisionData.id].checked = areAllSelected
        })
    }

    @action
    toggleAll = (clientId, val) => {
        if (this.client.id !== clientId) return
        Object.entries(this.checkboxOptions).forEach(([_, divisionData]) => {
            divisionData.checked = val
            divisionData.portfolios.forEach((p) => { p.checked = val })
        })
    }

    toggleCheckbox = (obj, e, oid) => {
        const valueIdStr = e.target.getAttribute('valueid')
        // console.log({ obj, oid, valueIdStr })
        const optionId = Number(valueIdStr)
        if (oid === 3) {
            this.toggleAll(this.client.id, obj.value)
        } else if (oid === 4) {
            this.toggleDivision(optionId, obj.value)
        } else if (oid === 5) {
            this.togglePortfolio(optionId, obj.value)
        }
    }

    updateTemplateAccess = async () => {
        this.updating = true
        const templateAccessBody = []
        const { checkboxOptions, divisionOptions, client } = this

        if (client.id === 1 && this.useLocalSave) {
            this.updating = false
            return exploriTemplateAccess
        }

        const divisionsDTO = []
        const portfoliosDTO = []
        const numOfDivisions = divisionOptions.length

        Object.entries(checkboxOptions).forEach(([_, divisionData]) => {
            if (divisionData.checked) {
                divisionsDTO.push({ oid: 4, rid: divisionData.id })
            } else {
                divisionData.portfolios.forEach((p) => {
                    if (p.checked) portfoliosDTO.push({ oid: 5, rid: p.id })
                })
            }
        })

        if (divisionsDTO.length === numOfDivisions) {
            templateAccessBody.push({ oid: 3, rid: client.id })
        } else {
            templateAccessBody.push(...divisionsDTO.map((data) => data))
            templateAccessBody.push(...portfoliosDTO.map((data) => data))
        }
        if (this.useLocalSave) {
            this.updating = false
            this.hasRequiredError = templateAccessBody.length < 1
            return templateAccessBody
        }

        try {
            const body = JSON.stringify(templateAccessBody)
            await this.store.http.post(`/template/${this.store.survey.id}/access`, body)
            this.store.addNotification({ body: 'Template access rights updated', level: 'success' })
        } catch (e) {
            console.error(e)
            this.store.handleError({ body: `Template access rights failed updating: ${e.message}`, level: 'error' })
        }
        this.updating = false
    }
}

export default TemplateAccessStore
